import React, { useEffect, useState } from "react";
import { Tabs, DatePicker, message, TimeRangePickerProps, Popover } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { CalendarOutlined } from "@ant-design/icons";
import API from "../api";
import style from "../style/tableCol.module.scss";
import BookingTable from "./bookingTable";
import useIsMobile from "../utils/useIsMobile";
import BookingDetailsDrawer from "./BookingDetailsDrawer";
import { storage, SUB_DOMAIN_TYPE } from "../utils/storage";
import MobileDateRange from "../components/MobileDateRange";

const { TabPane } = Tabs;
const { RangePicker } = DatePicker;
const rangePresets: TimeRangePickerProps["presets"] = [
  { label: "Next 7 Days", value: [dayjs().add(-7, "d"), dayjs()] },
  { label: "Next 14 Days", value: [dayjs().add(-14, "d"), dayjs()] },
  { label: "Next 30 Days", value: [dayjs().add(-30, "d"), dayjs()] },
  { label: "Next 90 Days", value: [dayjs().add(-90, "d"), dayjs()] },
];
const Schedule = () => {
  const scheduleTabs = ["Pickup", "Dropoff", "Completed"];
  const isMobile = useIsMobile();
  const [currentKey, setCurrentKey] = useState(scheduleTabs[0]);
  const [bookingData, setBookingData] = useState([]);
  const [showDrawer, setShowDrawer] = useState(false);
  const [showDaterange, setShowDaterange] = useState(false);
  const [selectedData, setSelectedData] = useState([]);
  const [filterDates, setFilterDates] = useState<[Dayjs, Dayjs]>([
    dayjs().startOf("day"), // Default: Today at start of the day
    dayjs().add(1, "day").endOf("day"), // Default: Tomorrow at end of the day
  ]);
  const stationId = storage.getSelectedStation().id;
  const fetchBookingData = async () => {
    try {
      // Mapping booking and ride status based on tabs
      const statusMapping: Record<
        string,
        { bookingStatus: string; rideStatus: string }
      > = {
        Pickup: { bookingStatus: "BOOKED", rideStatus: "NOT_STARTED" },
        Dropoff: { bookingStatus: "BOOKED", rideStatus: "ONGOING" },
        Completed: { bookingStatus: "BOOKED", rideStatus: "COMPLETED" },
      };

      const { bookingStatus, rideStatus } = statusMapping[currentKey];

      // Constructing the request payload
      const requestPayload: Record<string, any> = {
        bookingStatus,
        rideStatus,
        stationId,
      };

      // Adding date range for the current tab
      if (filterDates) {
        const [start, end] = filterDates;

        if (currentKey === "Pickup") {
          requestPayload.pickupFrom = start.toISOString();
          requestPayload.pickupTo = end.toISOString();
        } else if (currentKey === "Dropoff" || currentKey === "Completed") {
          requestPayload.dropoffFrom = start.toISOString();
          requestPayload.dropoffTo = end.toISOString();
        }
      }

      console.log("API Request Payload:", requestPayload); // Debugging the payload

      // Determine if user is partner or admin and use appropriate API
      const userType = storage.getAuthDetails().userType;
      const response =
        userType === SUB_DOMAIN_TYPE.PARTNER
          ? await API.partner.GetBookingData(requestPayload)
          : await API.admin.GetBookingData(requestPayload);

      if (!API.validators.checkSuccessCode(response)) {
        message.error("Technical error while fetching booking data");
        return;
      }

      setBookingData(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching booking data:", error);
      message.error("Error fetching booking data");
    }
  };

  const handleDateRangeChange = (
    dates: [Dayjs | null, Dayjs | null] | null
  ) => {
    if (dates) {
      const [start, end] = dates;

      if (start && end && end.isBefore(start)) {
        message.error("End date cannot be earlier than start date.");
        return;
      }

      setFilterDates([
        start?.startOf("day") || dayjs().startOf("day"),
        end?.endOf("day") || dayjs().add(1, "day").endOf("day"),
      ]);
    }
  };

  const handleTabChange = (key: string) => {
    setCurrentKey(key);
    // Do NOT reset dates when switching tabs; retain the current `filterDates`.
  };

  useEffect(() => {
    fetchBookingData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentKey, filterDates]);

  return (
    <div>
      {showDrawer && (
        <BookingDetailsDrawer
          showDrawer={showDrawer}
          setShowDrawer={setShowDrawer}
          selectedData={selectedData}
          setSelectedData={setSelectedData}
          isSchedulePage
        />
      )}
      <div className={style["tabs-and-daterange"]}>
        {/* Tabs */}
        <Tabs
          type="card"
          size="middle"
          defaultActiveKey={currentKey}
          onChange={handleTabChange}
        >
          {scheduleTabs.map((tab) => (
            <TabPane tab={tab} key={tab} />
          ))}
        </Tabs>
        {isMobile ? (
          <Popover
            placement="right"
            content={
              <>
                <MobileDateRange
                  filterDates={filterDates}
                  setFilterDates={setFilterDates}
                />
              </>
            }
            title="Select Date Range"
            trigger="click"
          >
            <div
              onClick={() => setShowDaterange(!showDaterange)}
              className={style["calender-container"]}
            >
              <CalendarOutlined
                style={{ color: "#fff" }}
                onPointerEnterCapture={() => {}}
                onPointerLeaveCapture={() => {}}
              />
            </div>
          </Popover>
        ) : (
          <RangePicker
            onChange={handleDateRangeChange}
            value={filterDates}
            getPopupContainer={(trigger: any) => trigger.parentNode}
            presets={[
              {
                label: (
                  <span aria-label="Current Time to End of Day">Today</span>
                ),
                value: () => [dayjs(), dayjs().endOf("day")], // 5.8.0+ support function
              },
              ...rangePresets,
            ]}
            className={style["daterange-mobile"]}
          />
        )}
      </div>

      {/* Booking Table */}
      <BookingTable
        data={bookingData}
        setShowDrawer={setShowDrawer}
        setSelectedData={setSelectedData}
        isPickup={currentKey === "Pickup"}
        isDropOff={currentKey === "Dropoff"}
      />
    </div>
  );
};

export default Schedule;
